import React, { useEffect, useRef, useState } from 'react';
import { ProductCompareTable } from '@jbhi-fi/components/src/ProductCompare/ProductCompareTable/ProductCompareTable';
import { styles } from './ProductComparePage.css';
import { serviceHelper } from '../../../config/service-helper';
import { useProductCompare } from '@jbhi-fi/components/src/ProductCompare';
import { CompareResults } from '@jbhi-fi/apis/src/compare';
import EmptyGraphic from './empty-compare.svg';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import DeleteIcon from '@jbhi-fi/lanyard-ui/components/Icons/Delete';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner';
import { Breadcrumbs } from '../stack/components/Breadcrumbs/Breadcrumbs';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { ProductCompareActions } from '@jbhi-fi/components/src/ProductCompare/ProductCompareActions';
import { submitTracking } from '@jbhi-fi/components/src/Tracking/utils/shopifyActionTrackingHelper';
import { removeParamFromUrl } from '@jbhi-fi/utils/src/urls';
import { getTrackingReferrerFromUrl } from '../../../utils/siteUrls';
import { ProductCard } from '@jbhi-fi/components/src/ProductCard';
import { useProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { OnResultFunction, QrReader } from 'react-qr-reader';
import Alert from '@jbhi-fi/lanyard-ui/components/Alert';
import Modal from '@jbhi-fi/lanyard-ui/components/Modal';
import LoadingIcon from '@jbhi-fi/lanyard-ui/components/Icons/Loading';
import FullscreenCameraIcon from '@jbhi-fi/lanyard-ui/components/Icons/FullscreenCamera';

let trackingSubmitted = false;

export const ProductComparePage = () => {
  const {
    isAddedToCompare,
    addItemToCompare,
    compareHandleClick,
    shouldShowCompare,
    removeItemFromCompare,
    clearAllComparedItems,
    itemsToCompare,
  } = useProductCompare();
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
  const [compareData, setCompareData] = useState<CompareResults | undefined>();
  const [isTableSticky, setIsTableSticky] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldShowWarning, setShouldShowWarning] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [QRScanned, setQRScanned] = useState(false);
  const stickyTestRef = useRef(null);
  const hasItems = compareData?.hits.length;
  const headerElement = document.getElementById('shopify-section-header');
  const footerElement = document.getElementById('shopify-section-footer');
  const headerPlaceholderRef = useRef<HTMLDivElement>(null);
  const footerPlaceholderRef = useRef<HTMLDivElement>(null);
  const isDesktop = useBreakpoint('md', 'up');
  const isJbSalesLineEnabled = window.Jbhifi?.jbSalesLineEnabled;

  const fetchCompareData = async () => {
    setIsLoading(true);

    const params = new URLSearchParams(window.location.search);
    const shouldKeepExisting = params.get('keep') === 'true';
    const urlSkus = params.getAll('sku');

    if (!urlSkus.length) {
      setCompareData(undefined);
      setIsLoading(false);
      return;
    }

    let skus = urlSkus;
    if (shouldKeepExisting) {
      const existingSkus = itemsToCompare.map(item => item.sku);
      skus = [...new Set(existingSkus.concat(urlSkus))];
    }

    if (skus.length > 4) {
      skus = skus.slice(0, 4);
      setShouldShowWarning(true);
    }

    const searchParams = new URLSearchParams(skus.map(sku => ['sku', sku]));
    const endpoint = `${serviceHelper.themeApisUrl}/compare?${searchParams.toString()}`;
    const response = await fetch(endpoint);
    const json = await response.json();
    setCompareData(json);

    history.replaceState({}, '', `?${searchParams.toString()}`);
    setIsLoading(false);
  };

  // IntersectionObserver is used to detect when the table is sticky
  useEffect(() => {
    const ref = stickyTestRef.current;
    const observer = new IntersectionObserver(([e]) => setIsTableSticky(!e.isIntersecting), {
      threshold: [1],
    });
    if (ref) observer.observe(ref);
    return () => {
      if (ref) observer.unobserve(ref);
    };
  }, [stickyTestRef]);

  useEffect(() => {
    fetchCompareData();
  }, []);

  useEffect(() => {
    if (headerPlaceholderRef.current && headerElement) {
      headerPlaceholderRef.current.appendChild(headerElement);
    }
    if (footerPlaceholderRef.current && footerElement) {
      footerPlaceholderRef.current.appendChild(footerElement);
    }
  }, [headerElement, footerElement]);

  useEffect(() => {
    clearAllComparedItems();
    compareData?.hits.forEach(hit => addItemToCompare(hit.sku, hit.product_image));
    if (compareData && !trackingSubmitted) {
      submitTracking({
        event: 'productCompare',
        action: 'compareItems',
        trackingDescriptor: {
          page: getTrackingReferrerFromUrl(),
        },
        gtm: compareData.hits.map(hit => hit.google_tag_manager),
      });
      trackingSubmitted = true;
    }
  }, [compareData]);

  const handleRemoveItem = (sku: string) => {
    removeItemFromCompare(sku);
    setShouldShowWarning(false);
    removeParamFromUrl('sku', sku);
    fetchCompareData();
  };

  const handleRemoveAllItems = () => {
    clearAllComparedItems();
    window.history.replaceState({}, '', window.location.pathname);
    fetchCompareData();
  };

  const handleAddToCart = async (
    e: React.SyntheticEvent<Element, Event>,
    sku?: string,
    variantId?: number
  ): Promise<boolean> => {
    const product = compareData?.hits.find(product => product.sku === sku);
    if (product && onAddToCart) {
      return await onAddToCart(e, product, variantId, {
        page: 'comparePage',
        ecommerceExtraProps: {
          list: 'comparePage',
        },
      });
    }
    return false;
  };

  const onQRScan: OnResultFunction = (result: Parameters<OnResultFunction>[0]) => {
    if (result) {
      const data = result.getText();
      if (data.includes('/pages/compare') && data.includes('keep=true')) {
        window.location.href = data;
        setQRScanned(true);
      }
    }
  };

  return (
    <>
      <div className={styles.wrapper({ isTableSticky })}>
        <div className={styles.headerPlaceholder} ref={headerPlaceholderRef}></div>
        {isDesktop ? (
          <Breadcrumbs
            colorHex="#707070"
            className={styles.breadcrumbs}
            items={[
              {
                label: 'Home',
                href: '/',
              },
              {
                label: 'Compare',
                href: '#',
                isActive: true,
              },
            ]}
          />
        ) : null}
        <div className={styles.header}>
          <h1 className={styles.headerH1}>Compare</h1>
          {!isDesktop ? (
            <Button
              type="linkWithIcon"
              label="Scan QR"
              iconPosition="left"
              icon={<FullscreenCameraIcon width={20} height={20} />}
              onClick={() => setIsQRModalOpen(true)}
            />
          ) : null}
          {hasItems ? (
            <Button
              type="linkWithIcon"
              label="Clear all"
              iconPosition="left"
              icon={<DeleteIcon width={20} height={20} />}
              onClick={handleRemoveAllItems}
              classes={styles.clearAllButton}
              data-testid="compare-clear-all-button"
            />
          ) : null}
        </div>
        {shouldShowWarning ? (
          <Alert
            onDismiss={() => setShouldShowWarning(false)}
            showIcon
            closable
            classes={styles.warningAlert}
            heading="Compare limit reached"
            content="You can only compare a maximum of 4 products. To compare this product, please remove another first."
            type="warning"
          />
        ) : null}
        <div ref={stickyTestRef}></div>
        {isLoading ? (
          <div className={styles.loadingDiv}>
            <Spinner />
            <div>Loading products to compare...</div>
          </div>
        ) : (
          <>
            {isQRModalOpen ? (
              <Modal show size="fullScreen" onClose={() => setIsQRModalOpen(false)}>
                {QRScanned ? (
                  <LoadingIcon width="20vw" height="auto" />
                ) : (
                  <QrReader
                    constraints={{ facingMode: 'environment' }}
                    onResult={onQRScan}
                    containerStyle={{ width: '100vw' }}
                    videoStyle={{ height: 'auto' }}
                  />
                )}
              </Modal>
            ) : null}
            {hasItems ? (
              <div className={styles.tableWrapper({ leftAlign: compareData.hits.length < 3 })}>
                <ProductCompareTable
                  shouldShowAskForADeal={isJbSalesLineEnabled}
                  items={compareData}
                  productCards={compareData.hits.map((hit, i) => (
                    <ProductCard
                      key={hit.objectID}
                      {...AlgoliaHitTransformer.toProductCardProps(hit, {
                        trackingDescriptor: {
                          page: 'comparePage',
                          currency: getStoreCurrency(),
                          ecommerceExtraProps: { list: 'comparePage', position: i + 1 },
                        },
                      })}
                      {...productCartPropsOverrides}
                      variant="tablet"
                      getIsCompared={isAddedToCompare}
                      onCompare={compareHandleClick}
                      getShouldShowCompare={shouldShowCompare}
                      isLoggedIn={isLoggedIn}
                      onUpdateWishlist={handleUpdateWishlist}
                      getIsInWishlist={getIsInWishlist}
                      shouldHideBannerTag
                      shouldHidePromoTag
                      onAddToCart={
                        onAddToCart
                          ? (e, _sku, variantId, eventData) =>
                              onAddToCart(e, hit, variantId, eventData?.trackingDescriptor)
                          : undefined
                      }
                      tooltipVariant="small"
                    />
                  ))}
                  actionButtons={compareData.hits.map(hit => {
                    const compareActionProps = AlgoliaHitTransformer.toProductCardProps(hit);
                    return (
                      <ProductCompareActions key={hit.objectID} {...compareActionProps} onAddToCart={handleAddToCart} />
                    );
                  })}
                  onRemoveItem={handleRemoveItem}
                  onScanQRClick={() => setIsQRModalOpen(true)}
                />
              </div>
            ) : (
              <div className={styles.emptyContainer}>
                <EmptyGraphic />
                <span className={styles.emptyEmphasis}>{"It's looking a little bare to compare ..."}</span>
                <span>To compare products hit the Compare tickbox to add them to your list.</span>
                <Button label="Shop now" type="primary" onClick={() => (window.location.href = '/')} />
              </div>
            )}
          </>
        )}
        <p className={styles.disclaimerText}>
          ^Discounts apply to most recent previous ticketed/advertised price. As we negotiate on price, products are
          likely to have sold below ticketed/advertised price in stores prior to the discount offer. Prices may differ
          at airport and express stores.
        </p>
        <div id="compare-feedback-placeholder"></div>
        <div className={styles.footerPlaceholder} ref={footerPlaceholderRef}></div>
      </div>
    </>
  );
};
